import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Sparkles, CheckCircle2,
  ShieldCheck, Mic, Briefcase, User,
  CalendarDays, BarChart3, FileText, Users,
  Search, MessageCircle, Check, ChevronRight,
  Globe, Landmark, Camera
} from 'lucide-react';
import styles from './Product.module.scss';
import clsx from 'clsx';

// Images (using the ones generated)
const IMG_SKYLINE = '/assets/images/panama_city_luxury_skyline_1778198316523.png';
const IMG_CASCO = '/assets/images/panama_casco_luxury_collab_1778198332816.png';
const IMG_WELLNESS = '/assets/images/panama_luxury_wellness_lifestyle_1778198348730.png';

const Product = () => {
  const { t } = useTranslation('product');
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeTab, setActiveTab] = useState('brands');

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.23, 1, 0.32, 1] } }
  };
  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  const STEPS = [
    {
      icon: Search,
      stepNum: '01',
      title: t('steps.step1.title'),
      text: t('steps.step1.text'),
    },
    {
      icon: Users,
      stepNum: '02',
      title: t('steps.step2.title'),
      text: t('steps.step2.text'),
    },
    {
      icon: CalendarDays,
      stepNum: '03',
      title: t('steps.step3.title'),
      text: t('steps.step3.text'),
    },
    {
      icon: ShieldCheck,
      stepNum: '04',
      title: t('steps.step4.title'),
      text: t('steps.step4.text'),
    },
  ];

  return (
    <div className={styles.productPage}>

      {/* ==============================
          1. HERO
      ============================== */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div initial="hidden" animate="visible" variants={stagger} className={styles.heroInner}>
            <motion.div variants={slideUp} className={styles.badge}>
              <Sparkles size={13} /> {t('badge')}
            </motion.div>

            <motion.h1 variants={slideUp} className={styles.heroTitle}>
              {t('heroTitle')}
            </motion.h1>

            <motion.p variants={slideUp} className={styles.heroSubtitle}>
              {t('heroSubtitle')}
            </motion.p>

            <motion.div variants={slideUp} className={styles.heroActions}>
              <Link to="/register" className={styles.primaryBtn}>
                {t('heroActions.apply')} <ChevronRight size={16} />
              </Link>
              <Link to="/product" className={styles.secondaryBtn}>
                {t('heroActions.explore')}
              </Link>
            </motion.div>

            <motion.div variants={slideUp} className={styles.heroPills}>
              <span><Check size={12} /> {t('heroPills.network')}</span>
              <span><Check size={12} /> {t('heroPills.clarity')}</span>
              <span><Check size={12} /> {t('heroPills.alignment')}</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ==============================
          2. MOCKUP / PRODUCT PREVIEW
      ============================== */}
      <section className={styles.mockupSection}>
        <div className={styles.container}>
          <div className={styles.mockupShell}>
            <div className={styles.browserBar}>
              <div className={styles.browserDots}>
                <span></span><span></span><span></span>
              </div>
              <div className={styles.browserUrl}>{t('mockups.dashboard.url')}</div>
            </div>

            <div className={styles.dashboardMock}>
              <div className={styles.mockSidebar}>
                <div className={clsx(styles.mockSideItem, styles.active)}><Sparkles size={14} /> {t('mockups.dashboard.sidebar.ecosystem')}</div>
                <div className={styles.mockSideItem}><Users size={14} /> {t('mockups.dashboard.sidebar.network')}</div>
                <div className={styles.mockSideItem}><CalendarDays size={14} /> {t('mockups.dashboard.sidebar.orchestration')}</div>
                <div className={styles.mockSideItem}><FileText size={14} /> {t('mockups.dashboard.sidebar.assets')}</div>
                <div className={styles.mockSideItem}><BarChart3 size={14} /> {t('mockups.dashboard.sidebar.performance')}</div>
              </div>
              <div className={styles.mockMain}>
                <div className={styles.mockHeader}>
                  <span className={styles.mockTitle}>{t('mockups.dashboard.header.title')}</span>
                  <div className={styles.mockNewBtn}>{t('mockups.dashboard.header.new_btn')}</div>
                </div>
                <div className={styles.mockCards}>
                  <div className={styles.mockCampaignCard}>
                    <div className={styles.mockCampTop}>
                      <span className={styles.mockCampName}>{t('mockups.dashboard.cards.summer.name')}</span>
                      <span className={clsx(styles.mockStatus, styles.active)}>{t('mockups.dashboard.status.active')}</span>
                    </div>
                    <div className={styles.mockCampMeta}>
                      <span><Users size={11} /> {t('mockups.dashboard.cards.summer.meta_creators', { count: 8, total: 10 })}</span>
                      <span><CalendarDays size={11} /> {t('mockups.dashboard.cards.summer.meta_date')}</span>
                    </div>
                    <div className={styles.mockProgress}>
                      <div className={styles.mockProgressFill} style={{ width: '80%' }}></div>
                    </div>
                  </div>
                  <div className={styles.mockCampaignCard}>
                    <div className={styles.mockCampTop}>
                      <span className={styles.mockCampName}>{t('mockups.dashboard.cards.wellness.name')}</span>
                      <span className={clsx(styles.mockStatus, styles.review)}>{t('mockups.dashboard.status.reviewing')}</span>
                    </div>
                    <div className={styles.mockCampMeta}>
                      <span><Users size={11} /> {t('mockups.dashboard.cards.wellness.meta_creators', { count: 3, total: 5 })}</span>
                      <span><FileText size={11} /> {t('mockups.dashboard.cards.wellness.meta_deliverables', { count: 2 })}</span>
                    </div>
                    <div className={styles.mockProgress}>
                      <div className={styles.mockProgressFill} style={{ width: '55%' }}></div>
                    </div>
                  </div>
                  <div className={styles.mockCampaignCard}>
                    <div className={styles.mockCampTop}>
                      <span className={styles.mockCampName}>{t('mockups.dashboard.cards.gastronomy.name')}</span>
                      <span className={clsx(styles.mockStatus, styles.completed)}>{t('mockups.dashboard.status.completed')}</span>
                    </div>
                    <div className={styles.mockCampMeta}>
                      <span><Check size={11} /> {t('mockups.dashboard.cards.gastronomy.meta_creators', { count: 5, total: 5 })}</span>
                      <span><BarChart3 size={11} /> {t('mockups.dashboard.cards.gastronomy.meta_impressions')}</span>
                    </div>
                    <div className={styles.mockProgress}>
                      <div className={styles.mockProgressFill} style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          3. LATAM MARKET FOCUS (NEW)
      ============================== */}
      <section className={styles.latamSection}>
        <div className={styles.container}>
          <div className={styles.latamHeader}>
            <div className={styles.sectionEyebrow}>{t('latam.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('latam.title')}</h2>
            <p className={styles.sectionLead}>
              {t('latam.desc')}
            </p>
          </div>
          
          <div className={styles.latamGrid}>
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className={styles.latamMedia}
            >
              <img src={IMG_SKYLINE} alt="Panama City Skyline" />
              <div className={styles.latamMediaCaption}>
                <span>{t('latam.eyebrow')}</span>
              </div>
            </motion.div>
            <div className={styles.latamFeatures}>
              <div className={styles.latamFeature}>
                <div className={styles.latamIcon}>
                  <Landmark size={22} />
                </div>
                <div>
                  <h4>{t('latam.items.hub.title')}</h4>
                  <p>
                    {t('latam.items.hub.desc')}
                  </p>
                </div>
              </div>
              <div className={styles.latamFeature}>
                <div className={styles.latamIcon}>
                  <Camera size={22} />
                </div>
                <div>
                  <h4>{t('latam.items.aesthetic.title')}</h4>
                  <p>
                    {t('latam.items.aesthetic.desc')}
                  </p>
                </div>
              </div>
              <div className={styles.latamFeature}>
                <div className={styles.latamIcon}>
                  <Globe size={22} />
                </div>
                <div>
                  <h4>{t('latam.items.resonance.title')}</h4>
                  <p>
                    {t('latam.items.resonance.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          4. PAIN → SOLUTION STRIP
      ============================== */}
      <section className={styles.painSection}>
        <div className={styles.container}>
          <div className={styles.painGrid}>
            <div className={styles.painColumn}>
              <div className={styles.painLabel}>{t('pain_solution.pain.title')}</div>
              <ul>
                {t('pain_solution.pain.items', { returnObjects: true }).map((item, i) => (
                  <li key={i}><MessageCircle size={14} /> {item}</li>
                ))}
              </ul>
            </div>
            <div className={styles.painArrow}>→</div>
            <div className={clsx(styles.painColumn, styles.solutionColumn)}>
              <div className={styles.painLabel}>{t('pain_solution.solution.title')}</div>
              <ul>
                {t('pain_solution.solution.items', { returnObjects: true }).map((item, i) => (
                  <li key={i}><Check size={14} /> {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          5. HOW IT WORKS
      ============================== */}
      <section className={styles.stepsSection}>
        <div className={styles.container}>
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={stagger}
            className={styles.stepsHeader}
          >
            <motion.div variants={slideUp} className={styles.sectionEyebrow}>{t('stepsHeader.eyebrow')}</motion.div>
            <motion.h2 variants={slideUp} className={styles.sectionTitle}>{t('stepsHeader.title')}</motion.h2>
          </motion.div>
          <div className={styles.stepsGrid}>
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className={styles.stepCard}
              >
                <div className={styles.stepNum}>{s.stepNum}</div>
                <div className={styles.stepIconWrap}>
                  <s.icon size={22} />
                </div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==============================
          6. VISUAL STORYTELLING (NEW)
      ============================== */}
      <section className={styles.gallerySection}>
        <div className={styles.container}>
          <div className={styles.galleryGrid}>
            <div className={styles.galleryTile}>
              <img src={IMG_CASCO} alt="Fine Dining Collaboration" />
              <div className={styles.galleryOverlay}>
                <span>{t('gallery.gastronomy.badge')}</span>
                <h3>{t('gallery.gastronomy.title')}</h3>
              </div>
            </div>
            <div className={styles.galleryTile}>
              <img src={IMG_WELLNESS} alt="Premium Lifestyle Experience" />
              <div className={styles.galleryOverlay}>
                <span>{t('gallery.hospitality.badge')}</span>
                <h3>{t('gallery.hospitality.title')}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          7. DUAL AUDIENCE
      ============================== */}
      <section className={styles.audienceSection}>
        <div className={styles.container}>
          <div className={styles.audienceHeader}>
            <div className={styles.sectionEyebrow}>{t('audience.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('audience.title')}</h2>
            <div className={styles.audienceTabs}>
              <button
                className={clsx(styles.audienceTab, activeTab === 'brands' && styles.active)}
                onClick={() => setActiveTab('brands')}
              >
                <Briefcase size={15} /> {t('audience.tabs.brands')}
              </button>
              <button
                className={clsx(styles.audienceTab, activeTab === 'creators' && styles.active)}
                onClick={() => setActiveTab('creators')}
              >
                <User size={15} /> {t('audience.tabs.creators')}
              </button>
            </div>
          </div>
          <div className={styles.audienceBody}>
            <AnimatePresence mode="wait">
              <motion.ul
                key={activeTab}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className={styles.benefitList}
              >
                {t(`audience.benefits.${activeTab}`, { returnObjects: true }).map((b, i) => (
                  <li key={i}><CheckCircle2 size={18} /> {b}</li>
                ))}
              </motion.ul>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ==============================
          8. IA FEATURE HIGHLIGHT
      ============================== */}
      <section className={styles.iaSection}>
        <div className={styles.container}>
          <div className={styles.iaCard}>
            <div className={styles.iaLeft}>
              <div className={styles.sectionEyebrow}>{t('ia.eyebrow')}</div>
              <h2 className={styles.sectionTitle}>{t('ia.title')}</h2>
              <p>{t('ia.desc')}</p>
              <Link to="/register" className={styles.iaBtn}>{t('ia.cta')} <ChevronRight size={15} /></Link>
            </div>
            <div className={styles.iaRight}>
              <div className={styles.voiceMockup}>
                <div className={styles.voiceHeader}>
                  <Mic size={16} /> {t('ia.steps.capturing')}
                </div>
                <div className={styles.voiceWave}>
                  {[...Array(20)].map((_, i) => (
                    <div key={i} className={styles.bar} style={{ height: `${Math.random() * 24 + 8}px` }}></div>
                  ))}
                </div>
                <div className={styles.voiceGenerating}>
                  <Sparkles size={13} /> {t('ia.steps.generating')}
                </div>
                <div className={styles.briefPreview}>
                  <div className={styles.briefLine} style={{ width: '85%' }}></div>
                  <div className={styles.briefLine} style={{ width: '60%' }}></div>
                  <div className={styles.briefLine} style={{ width: '75%' }}></div>
                  <div className={styles.briefLine} style={{ width: '50%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          9. CTA FINAL
      ============================== */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaInner}>
            <div className={styles.sectionEyebrow}>{t('cta_final.eyebrow')}</div>
            <h2>{t('cta_final.title')}</h2>
            <p>{t('cta_final.desc')}</p>
            <div className={styles.ctaActions}>
              <Link to="/register" className={styles.ctaPrimary}>{t('cta_final.primary')}</Link>
              <Link to="/about" className={styles.ctaSecondary}>{t('cta_final.secondary')}</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Product;

